/** @jsxImportSource @emotion/react */
import { Typography } from '@quickbit/qb-design-system';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { ErrorComponent } from 'components';
import { RedirectWithReturnLink, useMixpanel } from 'context';
import { OnrampLayout } from 'onramp-pages/components';
import { useSetOnrampData, useOnrampData } from '../context';
import { createBuyPreview } from './api';

export const Buy = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { receiveOrderPreviewId, hasCardData, merchantName, autoProcess } =
    useOnrampData();
  const setOnrampData = useSetOnrampData();
  const { mixpanelTrackEvent } = useMixpanel({
    Product: 'TopUp',
    Page: 'Buy',
  });

  if (!receiveOrderPreviewId) {
    throw Error(
      `Buy - Invalid state ${JSON.stringify({
        receiveOrderPreviewId,
      })}`
    );
  }

  const { value, loading, error } = useAsync(async () => {
    const previewResponse = await createBuyPreview(receiveOrderPreviewId);
    mixpanelTrackEvent('Buy started', {
      description: 'User started the buy process.',
    });
    setOnrampData({
      buyPreviewId: previewResponse.id,
      cryptoAmount: previewResponse.amount,
    });
    return previewResponse;
  }, [receiveOrderPreviewId]);

  useEffect(() => {
    if (autoProcess && value) {
      if (!hasCardData) {
        navigate('/onramp/buy/cashier');
      } else {
        navigate('/onramp/buy/front');
      }
    }
  }, [autoProcess, value, navigate, hasCardData]);

  if (error) {
    if (error.errorCode === 'COMPLETED_BUY_ORDER') {
      return <RedirectWithReturnLink to={`/onramp`} />;
    }
    if (error.errorCode === 'COMPLETED_PREVIEW') {
      return <RedirectWithReturnLink to={`/onramp/completed`} />;
    }
    if (error.errorCode === 'DELETED_PREVIEW') {
      return <RedirectWithReturnLink to={`/onramp/cancelled`} />;
    }
    return <ErrorComponent error={error} tryAgainAction={() => navigate(-1)} />;
  }

  return (
    <OnrampLayout
      topChildren={
        <Typography variant="body">
          {formatMessage(
            {
              id: 'page.onramp.buy.proceedToComplete',
            },
            { merchantName }
          )}
        </Typography>
      }
      buttonProps={{
        onClick: () =>
          !hasCardData
            ? navigate('/onramp/buy/cashier')
            : navigate('/onramp/buy/front'),
        disabled: loading || !value,
        loading: loading,
        text: formatMessage({
          id: 'page.onramp.buy.button',
        }),
      }}
    ></OnrampLayout>
  );
};

export default Buy;
